import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

interface Popup {
  id: string;
  title: string;
  image_url: string;
  link_url: string | null;
  start_date: string;
  end_date: string;
}

const PopupModal: React.FC = () => {
  const [popups, setPopups] = useState<Popup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hideToday, setHideToday] = useState(false);

  useEffect(() => {
    fetchActivePopups();
  }, []);

  const fetchActivePopups = async () => {
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('popups')
      .select('*')
      .eq('is_active', true)
      .lte('start_date', now)
      .gte('end_date', now)
      .order('display_order', { ascending: true });

    if (error) {
      console.error('Error fetching popups:', error);
      return;
    }

    if (data && data.length > 0) {
      // localStorage에서 오늘 안 보기 체크 확인
      const hiddenPopups = getHiddenPopups();
      const visiblePopups = data.filter(popup => !hiddenPopups.includes(popup.id));

      if (visiblePopups.length > 0) {
        setPopups(visiblePopups);
        setIsVisible(true);
      }
    }
  };

  // 오늘 안 보기로 숨긴 팝업 ID 목록 가져오기
  const getHiddenPopups = (): string[] => {
    const today = new Date().toDateString();
    const stored = localStorage.getItem('hiddenPopups');

    if (!stored) return [];

    try {
      const parsed = JSON.parse(stored);
      // 날짜가 오늘이면 숨긴 팝업 목록 반환, 아니면 초기화
      if (parsed.date === today) {
        return parsed.ids || [];
      }
      return [];
    } catch {
      return [];
    }
  };

  // 오늘 안 보기 저장
  const hidePopupToday = (popupId: string) => {
    const today = new Date().toDateString();
    const hiddenPopups = getHiddenPopups();

    if (!hiddenPopups.includes(popupId)) {
      hiddenPopups.push(popupId);
    }

    localStorage.setItem('hiddenPopups', JSON.stringify({
      date: today,
      ids: hiddenPopups
    }));
  };

  const handleClose = () => {
    if (hideToday && popups[currentIndex]) {
      hidePopupToday(popups[currentIndex].id);
    }

    // 다음 팝업이 있으면 표시
    if (currentIndex < popups.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setHideToday(false);
    } else {
      setIsVisible(false);
    }
  };

  const handleImageClick = () => {
    const currentPopup = popups[currentIndex];
    if (currentPopup?.link_url) {
      // 외부 링크인지 확인
      if (currentPopup.link_url.startsWith('http')) {
        window.open(currentPopup.link_url, '_blank');
      } else {
        window.location.href = currentPopup.link_url;
      }
    }
    handleClose();
  };

  if (!isVisible || popups.length === 0) {
    return null;
  }

  const currentPopup = popups[currentIndex];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative bg-white rounded-lg shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* 닫기 버튼 */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 z-10 p-1 bg-white/80 hover:bg-white rounded-full shadow-md transition-colors"
          aria-label="닫기"
        >
          <XMarkIcon className="w-6 h-6 text-gray-700" />
        </button>

        {/* 팝업 이미지 */}
        <div
          className={`${currentPopup.link_url ? 'cursor-pointer' : ''}`}
          onClick={currentPopup.link_url ? handleImageClick : undefined}
        >
          <img
            src={currentPopup.image_url}
            alt={currentPopup.title}
            className="w-full h-auto"
          />
        </div>

        {/* 하단 컨트롤 */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={hideToday}
              onChange={(e) => setHideToday(e.target.checked)}
              className="w-4 h-4 text-[#48c9a6] border-gray-300 rounded focus:ring-[#48c9a6]"
            />
            <span className="ml-2 text-sm text-gray-600">오늘 하루 안 보기</span>
          </label>

          <div className="flex items-center gap-3">
            {popups.length > 1 && (
              <span className="text-xs text-gray-500">
                {currentIndex + 1} / {popups.length}
              </span>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded transition-colors"
            >
              닫기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupModal;
